import LinearProgress from "@material-ui/core/LinearProgress";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { SvgIconProps } from "@material-ui/core/SvgIcon";
import CheckCircleIcon from "@material-ui/icons/CheckCircleRounded";
import ErrorIcon from "@material-ui/icons/ErrorRounded";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUncheckedRounded";
import React, { ComponentPropsWithRef } from "react";
import { Color } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { NavigationListItem, NavigationListItemContent } from "./NavigationCommonElement";

export interface NavigationProcessProgressBarProps {
    /**
     * The value of the progress indicator. Value between 0 and 100.
     */
    value: number;
}

export const NavigationProcessProgressBar: PlainFunctionComponent<NavigationProcessProgressBarProps> = ({ value }) => {
    const classes = useStyles();
    return <LinearProgress classes={{ bar1Determinate: classes.bar1Determinate }} className={classes.progressBar} variant="determinate" value={value} />;
};

export interface NavigationProcessElementProps extends HasTestId {
    /**
     * Determines if the `NavigationDrawer` is expanded which cause a tooltip over the icon and slightly different styling. Defaults to `true` if `undefined`.
     *
     * Note: leave `undefined` or set to `true` if used within `burgerableMenu`
     */
    drawerExpanded?: boolean;
    /**
     * SVG-Icon to be displayed on the left side of a menu element if provided.
     */
    Icon: React.ElementType<SvgIconProps>;
    /**
     * Mandatory label / text of the menu element.
     */
    label: NonNullable<React.ReactNode>;
    /**
     * General purpose of this `onClick` property is for handling `onBurgerMenuClose` in `burgerableMenu` because
     * e.g. if the burgerable menu button stays because of routing to an internal link the burgeralbe menu should be closed.
     * Defaults to "do nothing".
     */
    onClick?: () => void;
    /**
     * Determines if the menu element is colored as selected. Defaults to `false` if not provided.
     */
    selected?: boolean;
}

export interface NavigationActionProcessElementBaseProps {
    onClick: () => void;
}

export interface NavigationLinkProcessElementBaseProps<LinkT extends React.ElementType> {
    Link: LinkT;
    LinkProps?: ComponentPropsWithRef<LinkT>;
}

export type NavigationActionProcessElementProps = NavigationProcessElementProps & NavigationActionProcessElementBaseProps;
// Note: With interface the property completion within `LinkProps` is working
export interface NavigationLinkProcessElementProps<LinkT extends React.ElementType> extends NavigationProcessElementProps, NavigationLinkProcessElementBaseProps<LinkT> {}

export const NavigationActionProcessElement: PlainFunctionComponent<NavigationActionProcessElementProps> = ({ drawerExpanded, Icon, label, onClick, selected, testId }) => (
    <NavigationListItem drawerExpanded={drawerExpanded} onClick={onClick} selected={selected} testId={testId}>
        <NavigationListItemContent drawerExpanded={drawerExpanded} Icon={Icon} label={label} selected={selected} />
    </NavigationListItem>
);

export function NavigationLinkProcessElement<LinkT extends React.ElementType>({
    drawerExpanded,
    Icon,
    Link,
    LinkProps,
    label,
    onClick,
    selected,
    testId,
}: NavigationLinkProcessElementProps<LinkT>): React.ReactElement<NavigationLinkProcessElementProps<LinkT>, string> {
    return (
        <NavigationListItem drawerExpanded={drawerExpanded} component={Link} onClick={onClick} selected={selected} testId={testId} {...LinkProps}>
            <NavigationListItemContent drawerExpanded={drawerExpanded} Icon={Icon} label={label} selected={selected} />
        </NavigationListItem>
    );
}

export const ProgressElementCheckedIcon: PlainFunctionComponent<undefined> = () => {
    const classes = useStyles();
    return <CheckCircleIcon className={classes.checkedIcon} />;
};
export const ProgressElementErrorIcon: PlainFunctionComponent<undefined> = () => {
    const classes = useStyles();
    return <ErrorIcon className={classes.errorIcon} />;
};
export const ProgressElementUncheckedIcon: PlainFunctionComponent<undefined> = () => {
    const classes = useStyles();
    return <RadioButtonUncheckedIcon className={classes.uncheckedIcon} />;
};

const useStyles = makeStyles(
    createStyles({
        bar1Determinate: {
            backgroundColor: Color.yellow1,
        },
        progressBar: {
            marginLeft: "24px",
            marginRight: "24px",
            marginBottom: "16px",
        },
        checkedIcon: {
            color: Color.green1,
        },
        errorIcon: {
            color: Color.red1,
        },
        uncheckedIcon: {
            color: Color.blue1,
        },
    }),
);
